import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExplorerGetForNormalisedCategoryItem } from '../../../server/types/infer-trpc-types.ts';
import type {
    BillingStatus,
    CustomDateRange,
    ExplorerSort,
    ExplorerViewFilters,
    VisibilityWindow,
} from '../../../server/utils/explorer-utils.ts';
import { api } from '../../../utils/api.ts';
import { safeParseBreadcrumbs } from '../../../utils/insights-utils.ts';
import { useConfigurableExplorerColumns } from '../hooks/use-configurable-explorer-columns.tsx';
import { useExplorerSorting } from '../hooks/use-explorer-sorting.tsx';
import useRowSelection from '../hooks/use-manage-explorer-filters.tsx';
import { usePreventExplorerUnnecissearyRerender } from '../hooks/use-prevent-explorer-unnecisseary-rerender.ts';
import { ExplorerDataTable } from './explorer-data-table.tsx';

type Props = {
    hideUnselectedRows?: boolean;
    searchString?: string;
    window: VisibilityWindow;
    customDateRange: CustomDateRange | undefined;
    filters: ExplorerViewFilters;
    billingStatus: BillingStatus;
    onFiltersChange: (filters: string[]) => void;
    sortingFilter: ExplorerSort | null;
    setSortingFilter: (sortingFilter: ExplorerSort | null) => void;
    yearPeriods: number;
};

export function ExplorerViewNormalisedCategory({
    hideUnselectedRows = false,
    searchString,
    window,
    customDateRange,
    filters,
    billingStatus,
    onFiltersChange,
    sortingFilter,
    setSortingFilter,
    yearPeriods,
}: Props) {
    const { t } = useTranslation();
    const { breadcrumbDepthsToHide, normalisedCategoryDepthsUsedInInsightsInExplorer } = useAppConfig();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- legacy code
    const normalisedCategoryDepth = normalisedCategoryDepthsUsedInInsightsInExplorer!;

    const { sorting, sortChangedInInterface } = useExplorerSorting(sortingFilter, setSortingFilter);

    const { filtersWithRerenderProtection } = usePreventExplorerUnnecissearyRerender(
        filters,
        'normalisedCategory',
        hideUnselectedRows,
    );

    const {
        data: dataRaw,
        isLoading,
        isFetching,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
    } = api.platoUser.query.explorer.getForNormalisedCategory.getListWithFilters.useInfiniteQuery(
        {
            searchString,
            hideUnselectedRows,
            filters: filtersWithRerenderProtection,
            customDateRange,
            billingStatus,
            window,
            sorting: sortingFilter,
            yearPeriods,
            normalisedCategoryDepth,
        },
        {
            getNextPageParam: (lastPage) => lastPage.nextCursor,
        },
    );

    const columns = useConfigurableExplorerColumns({
        columns: [
            {
                id: 'name',
                accessorKey: 'name',
                headerText: t('header_tab__NORMALISED_CATEGORY', {
                    ns: 'explorer',
                }),
                generateCell: (row) => {
                    /* eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                       -- we know our data is ExplorerGetForNormalisedCategoryItem which includes breadcrumbs */
                    const typedRow = row as ExplorerGetForNormalisedCategoryItem;
                    const breadcrumbs = safeParseBreadcrumbs(typedRow.breadcrumbs);
                    const filteredBreadcrumbs = breadcrumbs?.slice(breadcrumbDepthsToHide) || [];

                    // Get the parent category (last breadcrumb) and current category
                    const parentCategory =
                        filteredBreadcrumbs.length > 0 ? filteredBreadcrumbs.at(-1)?.title : '';
                    const categoryName = typedRow.name;

                    // Format as "parentCategoryName / categoryName"
                    const displayText = parentCategory ? `${parentCategory} / ${categoryName}` : categoryName;

                    return displayText;
                },
                size: 250,
            },
        ],
    });

    const data = useMemo(() => {
        return dataRaw ? dataRaw.pages.flatMap((page) => page.result) : [];
    }, [dataRaw]);

    const { rowSelection, setRowSelection } = useRowSelection<ExplorerGetForNormalisedCategoryItem, 'id'>(
        data,
        'id',
        onFiltersChange,
        filters.normalisedCategory,
    );

    return (
        <ExplorerDataTable
            data={data}
            columns={columns}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
            isFetching={isFetching}
            hasNextPage={hasNextPage}
            isLoading={isLoading}
            filters={filters}
            window={window}
            billingStatus={billingStatus}
            customDateRange={customDateRange}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            sorting={sorting}
            sortChangedInInterface={sortChangedInInterface}
        />
    );
}
